import React, { useState } from 'react';
import { X, Crown, Zap, Loader2 } from 'lucide-react';
import { useSubscription } from '../context/SubscriptionContext';
import { useTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const UpgradeModal = () => {
  const { showUpgradeModal, setShowUpgradeModal, upgradeMessage, refreshSubscription } = useSubscription();
  const { isDarkMode } = useTheme();
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const [processing, setProcessing] = useState(false);

  if (!showUpgradeModal) return null;

  const handleTryNow = async () => {
    setProcessing(true);
    try {
      const token = localStorage.getItem('tapInToken');
      const origin = window.location.origin;
      const response = await fetch(
        `${API_URL}/api/subscriptions/create-checkout?plan=${selectedPlan}&origin_url=${encodeURIComponent(origin)}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(data.detail || 'Failed to create checkout session');
        setProcessing(false);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Something went wrong. Please try again.');
      setProcessing(false);
    }
  };

  const handleClose = () => {
    setShowUpgradeModal(false);
    setProcessing(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center" onClick={handleClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className={`relative w-full max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden ${
          isDarkMode ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-gray-200'
        }`}
        onClick={(e) => e.stopPropagation()}
        data-testid="upgrade-modal"
      >
        {/* Header */}
        <div className="relative p-6 pb-4">
          <button
            onClick={handleClose}
            className={`absolute top-3 right-3 p-1.5 rounded-full ${isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'} transition-colors`}
            data-testid="close-upgrade-modal"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
              <Crown className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Go Premium</h2>
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Unlock the full Tap In experience</p>
            </div>
          </div>
          {upgradeMessage && (
            <div className={`mt-3 p-3 rounded-xl text-sm ${isDarkMode ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-red-50 text-red-600 border border-red-100'}`}>
              {upgradeMessage}
            </div>
          )}
        </div>

        {/* Plan Selection */}
        <div className="px-6 space-y-3 pt-2">
          {/* Yearly Plan */}
          <button
            onClick={() => setSelectedPlan('yearly')}
            className={`w-full p-4 pt-5 rounded-2xl border-2 transition-all text-left relative ${
              selectedPlan === 'yearly'
                ? isDarkMode
                  ? 'border-amber-400 bg-gradient-to-r from-amber-500/15 to-orange-500/10'
                  : 'border-amber-400 bg-gradient-to-r from-amber-50 to-orange-50'
                : isDarkMode ? 'border-gray-700 hover:border-gray-600' : 'border-gray-200 hover:border-gray-300'
            }`}
            data-testid="plan-yearly"
          >
            <span className="absolute -top-3 left-4 px-2.5 py-0.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[10px] font-bold rounded-full uppercase tracking-wider">
              Best Value
            </span>
            <div className="flex items-center justify-between">
              <div>
                <p className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Annual Plan</p>
                <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span className="line-through">$71.88</span>
                  <span className="text-green-500 font-semibold ml-1.5">Save $11.89</span>
                </p>
              </div>
              <div className="text-right">
                <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>$59.99</p>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>/year</p>
              </div>
            </div>
          </button>

          {/* Monthly Plan */}
          <button
            onClick={() => setSelectedPlan('monthly')}
            className={`w-full p-4 rounded-2xl border-2 transition-all text-left relative ${
              selectedPlan === 'monthly'
                ? isDarkMode
                  ? 'border-amber-400 bg-gradient-to-r from-amber-500/15 to-orange-500/10'
                  : 'border-amber-400 bg-gradient-to-r from-amber-50 to-orange-50'
                : isDarkMode ? 'border-gray-700 hover:border-gray-600' : 'border-gray-200 hover:border-gray-300'
            }`}
            data-testid="plan-monthly"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Monthly Plan</p>
                <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Most Popular</p>
              </div>
              <div className="text-right">
                <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>$5.99</p>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>/month</p>
              </div>
            </div>
          </button>
        </div>

        {/* Features */}
        <div className="px-6 mt-4">
          <p className={`text-xs font-semibold mb-2.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>WHAT YOU GET</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {[
              'Unlimited playlists',
              'Unlimited songs',
              '25 media uploads',
              selectedPlan === 'yearly' ? 'Priority support' : 'Cancel anytime',
              selectedPlan === 'yearly' ? '2 months free' : null,
            ].filter(Boolean).map((feature, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 flex-shrink-0" />
                <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Button */}
        <div className="p-6 pt-4">
          <button
            onClick={handleTryNow}
            disabled={processing}
            className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-amber-400 via-orange-500 to-amber-500 text-white font-bold text-base flex items-center justify-center gap-2 hover:from-amber-500 hover:via-orange-600 hover:to-amber-600 transition-all active:scale-[0.98] disabled:opacity-50 shadow-lg shadow-orange-500/25"
            data-testid="upgrade-cta-btn"
          >
            {processing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Redirecting to checkout...
              </>
            ) : (
              <>
                Try Now — {selectedPlan === 'yearly' ? '$59.99/yr' : '$5.99/mo'}
              </>
            )}
          </button>
          <p className={`text-center text-[10px] mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Secure payment powered by Stripe. Cancel anytime.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
